import { CircleAlert, LockKeyhole, ShieldCheck, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { BrandMark } from '@/app/Brand';
import { GOOGLE_CLIENT_ID, loadGoogleIdentity } from '@/features/auth/google';
import { useAuthStore } from '@/features/auth/store';
import { cn } from '@/lib/cn';
import { LoadingState, toast, useIsDark } from '@/ui';

export type AuthMode = 'register' | 'login';

const COPY: Record<AuthMode, { title: string; subtitle: string; button: 'signup_with' | 'signin_with' }> = {
  register: {
    title: 'Create your account',
    subtitle: 'Split bills with friends, flatmates and trips — and always know who owes whom.',
    button: 'signup_with',
  },
  login: {
    title: 'Welcome back',
    subtitle: 'Sign in to pick up where you left off.',
    button: 'signin_with',
  },
};

const PERKS: { icon: LucideIcon; text: string }[] = [
  { icon: LockKeyhole, text: 'Your groups and expenses are private to your account' },
  { icon: Users, text: 'Track any number of groups and members' },
  { icon: ShieldCheck, text: 'No new password — sign in securely with Google' },
];

/** The account's front door: "Create account" and "Sign in" share one Google flow. */
export function AuthPage({ mode }: { mode: AuthMode }) {
  const copy = COPY[mode];
  const navigate = useNavigate();
  const signInWithGoogle = useAuthStore((state) => state.signInWithGoogle);
  const isDark = useIsDark();

  const buttonRef = useRef<HTMLDivElement>(null);
  const [buttonReady, setButtonReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    let cancelled = false;

    loadGoogleIdentity()
      .then((google) => {
        const host = buttonRef.current;
        if (cancelled || !host) return;

        google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID!,
          ux_mode: 'popup',
          cancel_on_tap_outside: true,
          callback: async ({ credential }) => {
            setSubmitting(true);
            setError(null);
            try {
              const isNewUser = await signInWithGoogle(credential);
              const name = useAuthStore.getState().session?.user.name ?? '';
              toast.success(
                isNewUser ? `Welcome, ${name}! Your account is ready.` : `Signed in as ${name}.`
              );
              navigate('/', { replace: true });
            } catch (cause) {
              setError((cause as Error).message);
              setSubmitting(false);
            }
          },
        });

        host.replaceChildren();
        google.accounts.id.renderButton(host, {
          type: 'standard',
          theme: isDark ? 'filled_black' : 'outline',
          size: 'large',
          shape: 'pill',
          text: copy.button,
          logo_alignment: 'center',
          // GIS takes a pixel width (max 400); match the card's content width.
          width: Math.min(400, Math.max(200, Math.floor(host.clientWidth))),
        });
        setButtonReady(true);
      })
      .catch((cause: Error) => {
        if (!cancelled) setError(cause.message);
      });

    return () => {
      cancelled = true;
    };
  }, [copy.button, isDark, navigate, signInWithGoogle]);

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="rounded-3xl border border-line bg-surface p-6 shadow-pop sm:p-8">
          <div className="text-center">
            <BrandMark size="lg" className="mx-auto" />
            <h1 className="mt-5 text-2xl font-bold tracking-tight">{copy.title}</h1>
            <p className="mt-2 text-sm text-muted">{copy.subtitle}</p>
          </div>

          {mode === 'register' && (
            <ul className="mt-6 space-y-2.5">
              {PERKS.map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-start gap-3 text-[13px]">
                  <span className="inline-flex size-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Icon size={15} strokeWidth={2.2} />
                  </span>
                  <span className="pt-1">{text}</span>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-7">
            {!GOOGLE_CLIENT_ID ? (
              <Notice>
                Google sign-in isn&rsquo;t configured for this build. Set{' '}
                <code className="rounded bg-elevated px-1 py-0.5 text-[12px]">VITE_GOOGLE_CLIENT_ID</code>{' '}
                and rebuild.
              </Notice>
            ) : (
              <>
                {submitting && <LoadingState label="Signing you in…" />}
                <div
                  ref={buttonRef}
                  className={submitting ? 'hidden' : 'flex min-h-11 w-full justify-center'}
                  aria-busy={!buttonReady}
                />
                {!buttonReady && !error && !submitting && (
                  <p className="mt-2 text-center text-[13px] text-muted">Loading Google sign-in…</p>
                )}
              </>
            )}

            {error && <Notice className="mt-4">{error}</Notice>}
          </div>

          <p className="mt-6 text-center text-[13px] text-muted">
            {mode === 'register' ? (
              <>
                Already have an account?{' '}
                <Link to="/login" className="font-semibold text-primary hover:underline">
                  Sign in
                </Link>
              </>
            ) : (
              <>
                New here?{' '}
                <Link to="/register" className="font-semibold text-primary hover:underline">
                  Create an account
                </Link>
              </>
            )}
          </p>
        </div>

        <p className="mt-4 text-center text-[12px] text-muted">
          We only use your Google name, email address and profile photo.
        </p>
      </div>
    </div>
  );
}

function Notice({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      role="alert"
      className={cn(
        'flex items-start gap-2.5 rounded-xl bg-danger/10 px-3.5 py-3 text-[13px] text-danger',
        className
      )}
    >
      <CircleAlert size={16} className="mt-px shrink-0" />
      <span>{children}</span>
    </div>
  );
}
